import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';

function normalizeKey(str) {
  return String(str ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function normalizeShotVariant(str) {
  return normalizeKey(str).replace(/\s+shot$/, '');
}

/**
 * Creatable Autocomplete backed by a dictionary.
 * Pass singleSelect=true to restrict to one value (returns a string instead of an array).
 */
export function MuiCreatableSelect({
  optionList,
  selected,
  baseItem,
  selectedFieldName,
  changeHandler,
  addOption,
  label,
  placeholder,
  id,
  readOnly,
  lockField,
  unlockField,
  singleSelect,
  onAfterChange,
  sx,
}) {
  const fieldId = id || `mui-select-${selectedFieldName}`;

  const createdOptionsRef = useRef(new Map());
  const [createdBump, setCreatedBump] = useState(0);

  // singleSelect: clear on focus so user can immediately type a replacement
  const [singleInputValue, setSingleInputValue] = useState(
    singleSelect ? String(selected ?? '').trim() : ''
  );
  const isFocusedRef = useRef(false);

  const baseOptions = useMemo(() => {
    if (!Array.isArray(optionList)) return [];
    return optionList
      .map((o) => {
        if (o == null) return null;
        if (typeof o === 'string') return { value: o, label: o };
        const value = o.value ?? o.id ?? o.key ?? o.label ?? o.name;
        const lbl = o.label ?? o.name ?? o.value ?? String(value ?? '');
        if (value == null || lbl == null) return null;
        return { value: String(value), label: String(lbl) };
      })
      .filter(Boolean);
  }, [optionList]);

  const options = useMemo(() => {
    const merged = [...baseOptions];
    for (const opt of createdOptionsRef.current.values()) merged.push(opt);
    const seen = new Set();
    return merged.filter((o) => {
      const k = normalizeKey(o.value);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseOptions, createdBump]);

  const optionByValueKey = useMemo(() => {
    const m = new Map();
    for (const o of options) m.set(normalizeKey(o.value), o);
    return m;
  }, [options]);

  const optionByLabelKey = useMemo(() => {
    const m = new Map();
    for (const o of options) m.set(normalizeKey(o.label), o);
    return m;
  }, [options]);

  const resolveToOption = useCallback(
    (raw) => {
      const rawStr = String(raw ?? '').trim();
      if (!rawStr) return null;

      const byValue = optionByValueKey.get(normalizeKey(rawStr));
      if (byValue) return byValue;

      const byLabel = optionByLabelKey.get(normalizeKey(rawStr));
      if (byLabel) return byLabel;

      const shotNormalized = normalizeShotVariant(rawStr);
      const byValueShot = optionByValueKey.get(shotNormalized);
      if (byValueShot) return byValueShot;

      const byLabelShot = optionByLabelKey.get(shotNormalized);
      if (byLabelShot) return byLabelShot;

      const matches = options.filter((o) => {
        const v = normalizeKey(o.value);
        const l = normalizeKey(o.label);
        const r = normalizeKey(rawStr);
        const rs = normalizeShotVariant(rawStr);
        return v.startsWith(r) || l.startsWith(r) || v.startsWith(rs) || l.startsWith(rs);
      });
      if (matches.length === 1) return matches[0];

      return { value: rawStr, label: rawStr, __unmatched: true };
    },
    [optionByValueKey, optionByLabelKey, options],
  );

  const handleMaybeAddOption = useCallback(
    async (rawText) => {
      const trimmed = String(rawText || '').trim();
      if (!trimmed) return null;

      const existing = resolveToOption(trimmed);
      if (existing && !existing.__unmatched) return existing;

      if (!addOption) return { value: trimmed, label: trimmed };

      let newOption = { value: trimmed, label: trimmed };
      try {
        const updated = await addOption(newOption, selectedFieldName);
        if (updated) {
          const v = updated.value ?? updated.id ?? updated.key ?? trimmed;
          const l = updated.label ?? updated.name ?? trimmed;
          newOption = { value: String(v), label: String(l) };
        }
      } catch (_e) {
        // swallow
      }

      const vKey = normalizeKey(newOption.value);
      createdOptionsRef.current.set(vKey, newOption);
      setCreatedBump((n) => n + 1);

      return newOption;
    },
    [addOption, selectedFieldName, resolveToOption],
  );

  // --- value resolution ---

  const selectedTokens = useMemo(() => {
    if (singleSelect) return [];
    if (selected == null) return [];
    if (Array.isArray(selected)) return selected.map((v) => String(v)).filter(Boolean);
    if (typeof selected === 'string')
      return selected
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    return [String(selected)].filter(Boolean);
  }, [selected, singleSelect]);

  const multiValue = useMemo(() => {
    return selectedTokens
      .map((t) => resolveToOption(t))
      .filter(Boolean)
      .map((o) => ({ value: String(o.value), label: String(o.label) }));
  }, [selectedTokens, resolveToOption]);

  const singleValue = useMemo(() => {
    if (!singleSelect) return undefined;
    const raw = String(selected ?? '').trim();
    if (!raw) return null;
    const resolved = resolveToOption(raw);
    if (!resolved) return null;
    return { value: String(resolved.value), label: String(resolved.label) };
  }, [singleSelect, selected, resolveToOption]);

  // Sync inputValue with external value changes (e.g. AI updates) when not actively editing
  const singleLabel = singleValue?.label ?? '';
  useEffect(() => {
    if (!singleSelect || isFocusedRef.current) return;
    setSingleInputValue(singleLabel);
  }, [singleSelect, singleLabel]);

  // --- change handlers ---

  const handleMultiChange = useCallback(
    async (_e, newValue) => {
      const normalizedObjects = [];
      for (const item of newValue || []) {
        if (typeof item === 'string') {
          const created = await handleMaybeAddOption(item);
          if (created) normalizedObjects.push(created);
        } else if (item && typeof item === 'object') {
          const canonical = resolveToOption(item.value ?? item.label);
          normalizedObjects.push(canonical || item);
        }
      }
      const valuesOnly = normalizedObjects
        .map((o) => resolveToOption(o.value ?? o.label) || o)
        .map((o) => String(o.value ?? '').trim())
        .filter(Boolean);
      changeHandler(baseItem.id, selectedFieldName, valuesOnly);
    },
    [handleMaybeAddOption, resolveToOption, changeHandler, baseItem, selectedFieldName],
  );

  const handleSingleChange = useCallback(
    async (_e, newValue, reason) => {
      const isCommit = reason === 'selectOption' || reason === 'createOption';
      if (reason === 'clear' || !newValue) {
        changeHandler(baseItem.id, selectedFieldName, '');
        return;
      }
      let resolved;
      if (typeof newValue === 'string') {
        resolved = await handleMaybeAddOption(newValue);
      } else {
        resolved = resolveToOption(newValue.value ?? newValue.label) || newValue;
      }
      if (resolved) {
        changeHandler(baseItem.id, selectedFieldName, String(resolved.value ?? '').trim());
      }
      if (isCommit) onAfterChange?.();
    },
    [handleMaybeAddOption, resolveToOption, changeHandler, baseItem, selectedFieldName, onAfterChange],
  );

  const defaultMultiSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 44,
      alignItems: 'center',
      bgcolor: '#fff',
    },
    '& .MuiAutocomplete-inputRoot': { bgcolor: '#fff' },
    '& .MuiChip-root': { height: 24 },
    '& .MuiChip-label': { paddingTop: 0, paddingBottom: 0 },
    '& .MuiAutocomplete-input': {
      paddingTop: '6px !important',
      paddingBottom: '6px !important',
    },
  };

  return (
    <Autocomplete
      multiple={!singleSelect}
      disableClearable={!singleSelect}
      freeSolo={!!addOption}
      id={fieldId}
      options={options}
      value={singleSelect ? singleValue : multiValue}
      disabled={!!readOnly}
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        return opt?.label ?? String(opt?.value ?? '');
      }}
      isOptionEqualToValue={(opt, val) =>
        normalizeKey(opt?.value) === normalizeKey(val?.value) ||
        normalizeKey(opt?.label) === normalizeKey(val?.label)
      }
      {...(singleSelect
        ? {
            inputValue: singleInputValue,
            onInputChange: (_e, val) => setSingleInputValue(val),
          }
        : {})}
      onOpen={() => lockField?.(fieldId)}
      onClose={(_e, reason) => {
        if (singleSelect && reason === 'escape') {
          setSingleInputValue(singleLabel);
        }
        unlockField?.(fieldId);
      }}
      onChange={singleSelect ? handleSingleChange : handleMultiChange}
      sx={sx ?? defaultMultiSx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          slotProps={label ? { inputLabel: { shrink: true } } : undefined}
          onFocus={() => {
            if (singleSelect) {
              isFocusedRef.current = true;
              setSingleInputValue('');
            }
            lockField?.(fieldId);
          }}
          onBlur={() => {
            if (singleSelect) {
              isFocusedRef.current = false;
              setSingleInputValue(singleLabel);
            }
            unlockField?.(fieldId);
          }}
          sx={
            sx
              ? undefined
              : {
                  '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
                }
          }
        />
      )}
    />
  );
}

export default MuiCreatableSelect;
